import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { REACH_STATS_HEADLINE, SERVICES } from "@/lib/content";
import { ArrowUpRight } from "lucide-react";

export default function About() {
  return (
    <main data-testid="about-page" className="bg-zinc-950 pt-32 pb-20">
      <Seo
        title="About us"
        description="The story behind HighwaysIndustry.com - a media, content and brand-building business that has served the UK highways and infrastructure sector since 2011."
        path="/about"
      />
      <section className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex items-center gap-3">
          <span className="h-px w-12 bg-accent-blue" />
          <span className="label-eyebrow text-accent-blue">
            About · Create · Amplify · Position
          </span>
        </div>
        <h1 className="font-heading font-black text-5xl md:text-7xl mt-6 max-w-4xl leading-[0.95] tracking-tighter">
          A content and media business
          <br />
          <span className="text-accent-blue">built for highways.</span>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-3xl leading-relaxed">
          HighwaysIndustry.com is not a marketing agency. We&apos;re a content,
          media, branding and communications business built specifically for
          the highways and infrastructure sector, and the team behind one of
          the largest dedicated highways audiences in the UK.
        </p>
      </section>

      {/* Who we are */}
      <section className="mt-20 border-y border-zinc-800 py-20 md:py-28 bg-black relative overflow-hidden">
        <div className="absolute inset-0 blueprint-dots opacity-40" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Who We Are
              </span>
            </div>
            <h2 className="font-heading font-black text-4xl md:text-5xl mt-4 leading-[0.95] tracking-tighter">
              We create content.
              <br />
              We grow audiences.
              <br />
              <span className="text-accent-blue">We build brands.</span>
            </h2>
            <p className="mt-6 text-zinc-300 leading-relaxed max-w-md">
              We help highways and infrastructure businesses become known for
              the things they actually do well, through video, photography,
              content, social and distribution.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
            {REACH_STATS_HEADLINE.map((s) => (
              <div
                key={s.label}
                className="bg-zinc-950 p-6 md:p-10"
                data-testid={`about-stat-${s.label.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "")}`}
              >
                <p className="font-heading font-black text-4xl md:text-6xl text-accent-blue tracking-tighter">
                  {s.value}
                </p>
                <p className="text-xs md:text-sm text-zinc-400 mt-3">
                  {s.label}
                </p>
                {s.note && (
                  <p className="text-[10px] md:text-xs text-zinc-500 mt-1">
                    {s.note}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we're different */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Why we&apos;re different
              </span>
            </div>
            <h2 className="font-heading font-black text-3xl md:text-4xl mt-4 leading-tight tracking-tight">
              Practical.
              <br />
              Commercial.
              <br />
              <span className="text-accent-blue">Industry-focused.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6">
            {[
              {
                t: "We only work in highways.",
                d: "Every video, photograph, article and social post is made by people who understand the sector, what looks credible, what wins bids, what reads on a hard hat.",
              },
              {
                t: "We publish, we don't just pitch.",
                d: "We are the editorial team behind HighwaysIndustry.com. We don't pitch journalists; we are them.",
              },
              {
                t: "We measure what matters.",
                d: "Not impressions. Real outcomes, framework wins, named mentions, inbound enquiries from the right people.",
              },
            ].map((row, i) => (
              <div
                key={i}
                className="border border-zinc-800 bg-zinc-900/30 p-6 md:p-8"
                data-testid={`about-difference-${i}`}
              >
                <span className="font-mono text-xs text-accent-blue">
                  0{i + 1}
                </span>
                <h3 className="font-heading font-bold text-xl md:text-2xl mt-3 tracking-tight">
                  {row.t}
                </h3>
                <p className="text-zinc-400 mt-3 leading-relaxed">{row.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-black border-t border-zinc-900 py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              Want to see what we can do for you?
            </h2>
            <p className="mt-6 text-zinc-400 max-w-md">
              Book a 30-minute discovery call. We&apos;ll talk through your
              business, what you want to be known for, and whether we&apos;re
              the right team for the job.
            </p>
            <Link
              to="/positioning-review"
              data-testid="about-cta-discovery"
              className="mt-8 inline-flex items-center gap-2 bg-primary-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest hover:glow-blue transition-all"
            >
              Book A Discovery Call <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-3 items-start">
            {SERVICES.slice(0, 6).map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`about-service-${s.slug}`}
                className="border border-zinc-700 hover:border-accent-blue px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                {s.label}
              </Link>
            ))}
            <Link
              to="/what-we-do"
              className="text-xs font-bold uppercase tracking-widest text-accent-blue inline-flex items-center gap-1"
            >
              All <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
